import React, { useCallback, useEffect, useMemo, useState , useRef} from "react";
import { useDispatch, useSelector } from "react-redux";

// mui components
import { Box, IconButton, Stack, Tab, Tabs } from "@mui/material";

// custom components
import { TabPanel } from "../../tab-panel";
import LoanInfoCard from "../../info-card";
import PrimaryButton from "../../buttons/primary-button";
import Heading6 from "../../typography/heading-06";
import RequiredError from "../../required-error";
import BorrowerInformation from "../../loan-form-card/borrower-information";
import PropertyDetails from "../../loan-form-card/property-details";
import LoanOriginator from "../../loan-form-card/loan-originator";
import LoanProcessor from "../../loan-form-card/loan-processor";
import AdditionalLoanContact from "../../loan-form-card/additional-loan-contact";

// mui icons
import {
	AddOutlined,
	ArrowForwardOutlined,
	CloseOutlined,
} from "@mui/icons-material";

// api slice
import { useUpdateBorrowerPairMutation } from "../../../features/update-borrower-pair/updateBorrowerPairApiSlice";
import { useFieldMutation } from "../../../features/field-values/fieldValueApiSlice";
import { useGetBorrowerPairMutation } from "../../../features/get-borrower-pair/getBorrowerPairApiSlice";
import { useGetLoanMutation } from "../../../features/get-loan-data/getLoanApiSlice";
import { useDeleteBorrowerPairMutation } from "../../../features/delete-borrower-pair/deleteBorrowerPairApiSlice";
import { useCreateBorrowerPairMutation } from "../../../features/create-borrower-pair/createBorrowerPairApiSlice";
import { useUpdateFieldsMutation } from "../../../features/update-fields/updateFieldsApiSlice";
import { useFetchAEEmailsMutation } from "../../../features/fetch-ae-emails/fetchAEEmailsApiSlice";
import { useUpdateMilestoneFreeRolesMutation } from "../../../features/update-milestone-free-roles/updateMilestoneFreeRolesApiSlice";
import { useGetBrokerListByTitleBranchMutation } from "../../../features/broker-search-title-branch/brokerSearchTitleBranchApiSlice";
import { useGetOrganizationDetailsMutation } from "../../../features/organization-detail/organizationDetailApiSlice";
import { useUpdateLoanMutation } from "../../../features/update-loan/updateLoanApiSlice";


// reducer slice
import { setBrokerListReducer } from "../../../features/broker-list/brokerListSlice";
import { 
	selectUserDetails,
	selectSelectedOrganization,
	selectOrganizationName,
} from "../../../features/auth/authSlice";
import { selectImpersonatedUser } from "../../../features/admin/impersonated-user/impersonatedUserSlice";
// custom modal
import LoaderModal from "../../modal/loader-modal";
import ErrorModal from "../../modal/error-modal";
import HelpTextModal from "../../modal/help-text-modal";

// helper utils
import {
	requiredFields,
	requiredFieldsForAdditionalContact,
	requiredFieldsForManualLoanOriginator,
} from "../../../utils/requiredFields";
import { handleSetPropertyDetails } from "../../../utils/loan-submission/set-property-details";
import {
	BROKER_ADMIN,
	LOAN_CHANNEL_CONSTANTS,
	TPO_SECONDARY,
} from "../../../utils/common-constants";
import {
	selectedUser,
	selectedUserWithLoanChannel,
	isInvalidLoLpName,
	buildLoLpFullName,
	resolveBrokerFromList,
} from "../../../utils/common-helpers";

import { borrowerCitizenshipTypeOptions } from "../../../utils/select-options";
import { runSilentValidation } from "../../../utils/silent-validation";
import { getOriginatorCompanyName } from "../../../utils/originator-company-name";
import { getSavedDbaCompanyName } from "../../../utils/company-representation";
import { buildTpoFileContactFieldValues } from "../../../utils/tpo-file-contacts";
import useRegisterLoanSection from "../../../hooks/useRegisterLoanSection";

// custom styles
import styles from "./index.module.scss";

export const EditLoanApplication = ({
	setLoanApplicationStage,
	applicationData,
	setApplicationData,
	loanGuid,
	organizationName,
	childOrganizationName,
	currentStage,
	registerSectionHandler,
}) => {
	console.log("24 application data edit:", applicationData);
	console.log("45 loan id:", loanGuid);

	const dispatch = useDispatch();
	const userDetails = useSelector(selectUserDetails);
	const selectedOrganization = useSelector(selectSelectedOrganization);
	const impersonatedUser = useSelector(selectImpersonatedUser);

	// Determine the current organization for API calls
	let currentOrganization = selectedOrganization || organizationName;
	
	// Fallback: if no org selected and user has multiple orgs, use the first one
	if (!currentOrganization && userDetails?.organizations?.length > 0) {
		currentOrganization = userDetails.organizations[0].name;
	}

	const [getBorrowerPair, { isLoading: loadingBorrowerPairData }] =
		useGetBorrowerPairMutation();

	const [field, { isLoading: isFetchingFields }] = useFieldMutation();

	const [getLoan, { isLoading: loadingloanData }] = useGetLoanMutation();

	const [updateFields, { isLoading: isUpdatingFields }] =
		useUpdateFieldsMutation();
	const firstTimeHomebuyerSelections = useRef(new Map());

	const setFirstTimeHomebuyerSelection = useCallback((borrowerIndex, value) => {
		firstTimeHomebuyerSelections.current.set(borrowerIndex, value);
	}, []);

	const updateFirstTimeHomebuyerFields = useCallback(async () => {
		const fieldValues = Array.from(firstTimeHomebuyerSelections.current.entries())
			.reduce((values, [borrowerIndex, selection]) => {
				if (!selection) {
					return values;
				}

				const id = borrowerIndex % 2 === 0 ? "4973" : "4974";
				const fieldValue = { id, value: selection === "Yes", lock: true };
				const existingFieldIndex = values.findIndex((field) => field.id === id);

				if (existingFieldIndex >= 0) {
					values[existingFieldIndex] = fieldValue;
				} else {
					values.push(fieldValue);
				}

				return values;
			}, []);

		if (fieldValues.length > 0) {
			await updateFields({
				loan_guid: loanGuid,
				field_values: fieldValues,
			}).unwrap();
		}
	}, [loanGuid, updateFields]);

	const [updateLoan, { isLoading: updatingLoan }] = useUpdateLoanMutation();
	const [fetchAEEmails, { isLoading: isFetchingAEEmails }] = useFetchAEEmailsMutation();
	const [updateMilestoneFreeRoles] = useUpdateMilestoneFreeRolesMutation();


	const isInitialLOLoad = useRef(true);

	const [updateBorrowerPair, { isLoading: updatingBorrowerPair }] =
		useUpdateBorrowerPairMutation();

	const [createBorrowerPair, { isLoading: creatingBorrowerPair }] =
		useCreateBorrowerPairMutation();

	const [deleteBorrowerPair, { isLoading: deletingBorrowerPair }] =
		useDeleteBorrowerPairMutation();

	const isAdmin = userDetails?.user_roles?.includes("admin") === true;
	console.log("260 is admin:", isAdmin);

	const isExecutiveOrManager =
		userDetails?.user_roles?.includes("executive") ||
		userDetails?.user_roles?.includes("manager") ||
		userDetails?.user_roles?.includes("AE") ||
		userDetails?.user_roles?.includes("AM") ||
		userDetails?.user_roles?.includes("PA") ||
		userDetails?.user_roles?.includes("assistant");

	console.log("270 is executive or manager:", isExecutiveOrManager);

	const isTpoSecondary = userDetails?.access_level?.includes("TPO Secondary");

	console.log("260 is tpo secondary:", isTpoSecondary);

	let brokerSearchOrgName;
	let includeChildren;

	const isInternalUser =
		userDetails?.user_roles?.includes("executive") ||
		userDetails?.user_roles?.includes("manager") ||
		userDetails?.user_roles?.includes("AE") ||
		userDetails?.user_roles?.includes("AM") ||
		userDetails?.user_roles?.includes("PA") ||
		userDetails?.user_roles?.includes("assistant");

	// Note: the broker search API slice already URL-encodes org_name, so the
	// raw name must be passed here — pre-encoding double-encodes it and the
	// search returns an empty list (dropdowns can then never populate).
	if (
		isInternalUser ||
		selectedUser(userDetails, impersonatedUser)?.access_level ===
			TPO_SECONDARY ||
		selectedUser(userDetails, impersonatedUser)?.access_level === BROKER_ADMIN
	) {
		brokerSearchOrgName = currentOrganization;
		includeChildren = true;
	} else if (
		selectedUser(userDetails, impersonatedUser)?.child_org_name !== "" && !selectedOrganization
	) {
		brokerSearchOrgName = childOrganizationName;
		includeChildren = false;
	} else {
		brokerSearchOrgName = currentOrganization;
		includeChildren = false;
	}

	const regexNdcOrCorr = /\b(?:NDC|CORR)\b/i;
	const isOrgNdcOrCorr = regexNdcOrCorr.test(brokerSearchOrgName);

	console.log("is organization NDC or CORR:", isOrgNdcOrCorr);

	const allowManualInput =
		isAdmin ||
		(isOrgNdcOrCorr &&
			(isInternalUser ||
				selectedUserWithLoanChannel(userDetails)?.loan_channel ===
					LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails)?.loan_channel ===
					LOAN_CHANNEL_CONSTANTS.CORR));

	const [loanApplicationData, setLoanApplicationData] = useState([{}, {}]);
	const [borrowerResponseData, setBorrowerResponseData] = useState([]);

	const [foreignNationalIndex, setForeignNationalIndex] = useState([]);

	const [tabValue, setTabValue] = useState(0);

	const [propertyInfo, setPropertyInfo] = useState({});
	const [grossRent, setGrossRent] = useState("");

	const [homeownersInsurance, setHomeownersInsurance] = useState("");
	const [floodInsurance, setFloodInsurance] = useState("");
	const [propertyTaxes, setPropertyTaxes] = useState("");
	const [realEstateTaxes, setRealEstateTaxes] = useState("");
	const [hoaDues, setHoaDues] = useState("");

	const [getBrokerListByTitleBranch, { isLoading: brokerListBranchLoading }] =
		useGetBrokerListByTitleBranchMutation();

	const [getOrganizationDetails, { isLoading: organizationDetailsLoading }] =
		useGetOrganizationDetailsMutation();
	const [organizationDetails, setOrganizationDetails] = useState({});

	const [currentOrgName, setCurrentOrgName] = useState({});
	const [currentOrgDetails, setCurrentOrgDetails] = useState({});

	const [childOrganizationDetails, setChildOrganizationDetails] = useState({});

	const [loanOriginatorEmail, setLoanOriginatorEmail] = useState("");
	const [loanOriginatorId, setLoanOriginatorId] = useState("");
	const [loanProcessorId, setLoanProcessorId] = useState("");
	const [organizationUsers, setOrganizationUsers] = useState([]);
	const [loanOriginator, setLoanOriginator] = useState({});

	const [organizationLicense, setOrganizationLicense] = useState("");

	const [errorModalVisible, setErrorModalVisible] = useState(false);

	const handleOpenErrorModal = () => {
		setErrorModalVisible(true);
	};

	const handleCloseErrorModal = () => {
		setErrorModalVisible(false);
	};

	const [loanProcessorEmail, setLoanProcessorEmail] = useState("");
	const [organizationProcessor, setOrganizationProcessor] = useState([]);
	const [loanProcessor, setLoanProcessor] = useState({});

	const [additionalContact, setAdditionalContact] = useState({
		name: "",
		phone: "",
		email: "",
	});

	const [manualOriginator, setManualOriginator] = useState(false);
	// LC-481: 315 saved with a DBA ("[Legal Name] dba [DBA Name]") at loan creation
	const [savedDbaCompanyName, setSavedDbaCompanyName] = useState(null);
	const [originatorData, setOriginatorData] = useState({
		orgName: "",
		address: "",
		unitNumber: "",
		city: "",
		state: "",
		zipcode: "",
		orgNmls: "",
		orgStateLicense: "",
		originatorEmail: "",
		originatorFirstName: "",
		originatorLastName: "",
		originatorNmls: "",
		originatorStateLicense: "",
		originatorPhone: "",
	});

	const [helpText, setHelpText] = useState("");
	const [extraHelpText, setExtraHelpText] = useState("");
	const [helpTextOpen, setHelpTextOpen] = useState(false);

	const handleOpenHelpText = () => {
		setHelpTextOpen(true);
	};

	const handleCloseHelpText = () => {
		setHelpTextOpen(false);
	};

	useEffect(() => {
		const getBrokerListFunc = async () => {
			let searchResults = await getBrokerListByTitleBranch({
				orgName: brokerSearchOrgName,
				title: "Loan Officer",
				include_child: includeChildren,
			}).unwrap();

			dispatch(setBrokerListReducer(searchResults));

			let updatedArr = searchResults.map((item) => ({
				...item,
				label: item?.user?.email || "",
				value: item?.user?.email || "",
			}));

			console.log("150 loan originator updated:", updatedArr);
			setOrganizationUsers(updatedArr);
			
		};

		const getProcessorListFunc = async () => {
			let searchResults = await getBrokerListByTitleBranch({
				orgName: brokerSearchOrgName,
				title: "Loan Processor",
				include_child: includeChildren,
			}).unwrap();

			let updatedArr = searchResults.map((item) => ({
				...item,
				label: item?.user?.email || "",
				value: item?.user?.email || "",
			}));

			console.log("150 loan processor updated:", updatedArr);
			setOrganizationProcessor(updatedArr);
		};

		const getOrganizationDetailsFunc = async () => {
			const searchResults = await getOrganizationDetails({
				organizationName: encodeURIComponent(currentOrganization),
			}).unwrap();
			setOrganizationDetails(searchResults);
		};

		const getChildOrganizationDetailsFunc = async () => {
			const searchResults = await getOrganizationDetails({
				organizationName: encodeURIComponent(childOrganizationName),
			}).unwrap();

			setChildOrganizationDetails(searchResults);
		};

		console.log("220 organization name:", currentOrganization);
		if (
			currentOrganization !== null &&
			currentOrganization !== undefined &&
			currentOrganization !== ""
		) {
			getBrokerListFunc();
			getProcessorListFunc();
			getOrganizationDetailsFunc();

			// Only fetch child organization details if no organization is explicitly selected
			// and the user has a default child organization
			if (
				!selectedOrganization &&
				(((isExecutiveOrManager || isTpoSecondary) &&
					childOrganizationName !== "") ||
				selectedUser(userDetails, impersonatedUser)?.child_org_name !== "")
			) {
				getChildOrganizationDetailsFunc();
			}
		}
	}, [currentOrganization, selectedOrganization, userDetails]);

	useEffect(() => {
		// Simple priority: selectedOrganization > childOrganizationName > currentOrganization
		const priorityOrgName = selectedOrganization || childOrganizationName || currentOrganization;
		setCurrentOrgName(priorityOrgName);
		setCurrentOrgDetails(organizationDetails);
	}, [
		currentOrganization,
		organizationName,
		organizationDetails,
		childOrganizationName,
		selectedOrganization,
	]);

	useEffect(() => {
		if (
			loanOriginatorEmail && Object.keys(loanOriginatorEmail)?.length !== 0 ||
			loanOriginatorEmail !== ""
		) {
			const originatorDetails = resolveBrokerFromList(
				organizationUsers,
				loanOriginatorId,
				loanOriginatorEmail
			);

			console.log("[LOLP resolve] LO:", {
				listSize: organizationUsers?.length,
				loanOriginatorId,
				loanOriginatorEmail,
				matched: originatorDetails?.user?.email || null,
				matchedBrokerId: originatorDetails?.broker_id ?? null,
			});

			// Only overwrite when a match is found so a failed lookup (async list
			// load, id type mismatch) cannot clobber a valid loanOriginator object.
			if (originatorDetails) {
				setLoanOriginator(originatorDetails);
				// Sync the id so the dropdown displays the selection even when the
				// match was found via the email fallback (missing/stale saved id).
				setLoanOriginatorId(originatorDetails.broker_id);
			}
		}
	}, [loanOriginatorEmail, organizationUsers,loanOriginatorId]);

	useEffect(() => {
		// if (
		// 	loanProcessorEmail && Object.keys(loanProcessorEmail)?.length !== 0 ||
		// 	loanProcessorEmail !== ""
		// ) {
		const hasProcessorIdentifier =
			Boolean(String(loanProcessorEmail || "").trim()) ||
			Boolean(String(loanProcessorId || "").trim());

		if (hasProcessorIdentifier && organizationProcessor?.length > 0) {
			const processorDetails = resolveBrokerFromList(
				organizationProcessor,
				loanProcessorId,
				loanProcessorEmail
			);

			if (processorDetails) {
				setLoanProcessor(processorDetails);
				// setLoanProcessorId(processorDetails.broker_id);
				setLoanProcessorId(String(processorDetails.broker_id));
				if (!loanProcessorEmail && processorDetails?.user?.email) {
					setLoanProcessorEmail(processorDetails.user.email.toLowerCase());
				}
			}
		}
	}, [loanProcessorEmail, organizationProcessor, loanProcessorId]);

	useEffect(() => {
		if (isInitialLOLoad.current) {
			isInitialLOLoad.current = false;
			return;
		}
		if (!isAdmin || !loanOriginatorId || !loanGuid) return;

		const updateAEEmailForLO = async () => {
			try {
				const aeResult = await fetchAEEmails({
					broker_id:
						userDetails?.organizations?.find(org => org.name === currentOrganization)?.tpo_broker_id ||
						userDetails?.user_tpo_id ||
						impersonatedUser?.user_tpo_id,
					loan_processor_broker_id: loanOriginatorId,
				}).unwrap();
				await updateFields({
					loan_guid: loanGuid,
					field_values: [
						{ id: "CX.LC.AE.EMAIL.LO", value: aeResult?.loan_processor_ae_email || "" },
						{ id: "CX.LC.AE.EMAIL", value: aeResult?.loan_processor_ae_email || aeResult?.branch_ae_email || aeResult?.company_ae_email || "" },
					],
					selectedOrganization: currentOrganization,
					userDetails,
				}).unwrap();
			} catch (err) {
				console.warn("[AE Email] LO change fetch failed:", err);
			}
		};
		updateAEEmailForLO();
	}, [loanOriginatorId]);

	const [error, setError] = useState("");

	useEffect(() => {
		const getAllBorrowerPair = async () => {
			const borrowerPairData = await getBorrowerPair({
				loanGuid,

			}).unwrap();

			setBorrowerResponseData(borrowerPairData?.response);
		};

		if (loanGuid !== null && loanGuid !== undefined && loanGuid !== "") {
			getAllBorrowerPair();
		}
	}, [loanGuid]);

	useEffect(() => {
		const getLoanDetails = async () => {
			const loanData = await getLoan({
				loanid: loanGuid,

			}).unwrap();

			console.log("230 loan data:", loanData);

			if (loanData?.response !== undefined) {
				setGrossRent(
					loanData?.response?.subjectPropertyGrossRentalIncomeAmount
				);

				handleSetPropertyDetails(
					loanData?.response,
					setHomeownersInsurance,
					setFloodInsurance,
					setPropertyTaxes,
					setRealEstateTaxes,
					setHoaDues
				);
			}

			console.log("295 user details:", userDetails);

			if (isExecutiveOrManager) {
				setLoanOriginatorEmail(loanData?.response?.interviewerEmail);
			} else {
				setLoanOriginatorEmail(
					selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
						?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
						selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
							?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR
						? loanData?.response?.interviewerEmail
						: loanData?.response?.tPO?.lOEmail?.toLowerCase() || ""
				);
			}

			setLoanProcessorEmail(
				loanData?.response?.tPO?.lPEmail?.toLowerCase() || ""
			);

			// Hydrate LO/LP ids from the loan response immediately (kept as
			// strings — parseInt on a missing value produces NaN which matches
			// nothing). Field-value fallbacks are applied below once fetched.
			if (loanData?.response?.tPO?.lOID) {
				setLoanOriginatorId(String(loanData.response.tPO.lOID));
			}

			if (loanData?.response?.tPO?.lPID) {
				setLoanProcessorId(String(loanData.response.tPO.lPID));
			}
			

			setPropertyInfo(loanData?.response?.property || {});

			let borrowerPairs = loanData?.response?.applications
				?.map((application) => [application.borrower, application.coborrower])
				.flat();

			try {
				const getValues = await field({
					loan_guid: loanGuid,
					fields:
						"CX.FOREIGNNATLBORR1,CX.FOREIGNNATLBORR2,CX.FOREIGNNATLBORR3,CX.FOREIGNNATLBORR4,CX.FOREIGNNATLBORR5,CX.FOREIGNNATLBORR6,CX.LOLP.SELECT.MANUAL.METHOD,TPO.X61,TPO.X65,TPO.X62,TPO.X75,TPO.X63,TPO.X76,315,URLA.X188,URLA.X190,313,321,323,3237,3629,3968,URLA.X170,URLA.X172,3238,2306,1823,TPO.X24,TPO.X48",
					selectedOrganization: currentOrganization,
					userDetails,
				}).unwrap();

				
				console.log("175 all fields:", getValues?.response);
				console.log("240 borrowerPairs:", borrowerPairs);

				let updatedBorrowerPairs = [];
				let foreignIndex = [];

				if (getValues?.response?.length > 0) {
					// Hydrate LO/LP ids and emails from the saved Encompass fields
					// (TPO.X62/X75 ids, TPO.X63/X76 emails) when the loan tPO object
					// did not provide them. This must run before any code below that
					// might throw, so a partial field response cannot skip it.
					const getFieldValue = (fieldId) =>
						getValues?.response?.find((item) => item?.fieldId === fieldId)
							?.value || "";

					const fieldLoId = getFieldValue("TPO.X62");
					const fieldLpId = getFieldValue("TPO.X75");
					const fieldLoEmail = getFieldValue("TPO.X63").toLowerCase();
					const fieldLpEmail = getFieldValue("TPO.X76").toLowerCase();

					console.log("[LOLP hydrate] field values:", {
						fieldLoId,
						fieldLpId,
						fieldLoEmail,
						fieldLpEmail,
						tPO: loanData?.response?.tPO,
					});

					if (fieldLoId) {
						setLoanOriginatorId((prev) => prev || fieldLoId);
					}

					if (fieldLpId) {
						setLoanProcessorId((prev) => prev || fieldLpId);
					}

					if (fieldLoEmail) {
						setLoanOriginatorEmail((prev) => prev || fieldLoEmail);
					}

					if (fieldLpEmail) {
						setLoanProcessorEmail((prev) => prev || fieldLpEmail);
					}

					let manualOriginatorValue =
						getValues?.response?.find(
							(item) => item.fieldId === "CX.LOLP.SELECT.MANUAL.METHOD"
						)?.value === "Y";

					console.log("320 manual originator Value:", manualOriginatorValue);
					setManualOriginator(manualOriginatorValue);

					setSavedDbaCompanyName(
						getSavedDbaCompanyName({
							savedCompanyName: getFieldValue("315"),
							savedCompanyDba: getFieldValue("TPO.X24"),
							savedBranchDba: getFieldValue("TPO.X48"),
						})
					);

					if (manualOriginatorValue) {
						let orgName = getValues?.response?.find(
							(item) => item?.fieldId === "315"
						)?.value;
						let address = getValues?.response?.find(
							(item) => item?.fieldId === "URLA.X188"
						)?.value;
						let unitNumber = getValues?.response?.find(
							(item) => item?.fieldId === "URLA.X190"
						)?.value;
						let city = getValues?.response?.find(
							(item) => item?.fieldId === "313"
						)?.value;
						let state = getValues?.response?.find(
							(item) => item?.fieldId === "321"
						)?.value;
						let zipcode = getValues?.response?.find(
							(item) => item?.fieldId === "323"
						)?.value;
						let orgNmls = getValues?.response?.find(
							(item) => item?.fieldId === "3237"
						)?.value;
						let orgStateLicense = getValues?.response?.find(
							(item) => item?.fieldId === "3629"
						)?.value;
						let originatorEmail = getValues?.response?.find(
							(item) => item?.fieldId === "3968"
						)?.value;
						let originatorFirstName = getValues?.response?.find(
							(item) => item?.fieldId === "URLA.X170"
						)?.value;
						let originatorLastName = getValues?.response?.find(
							(item) => item?.fieldId === "URLA.X172"
						)?.value;
						let originatorNmls = getValues?.response?.find(
							(item) => item?.fieldId === "3238"
						)?.value;
						let originatorStateLicense = getValues?.response?.find(
							(item) => item?.fieldId === "2306"
						)?.value;
						let originatorPhone = getValues?.response?.find(
							(item) => item?.fieldId === "1823"
						)?.value;

						console.log(
							"380 manual originator data:",
							orgName,
							address,
							unitNumber,
							city,
							state,
							zipcode,
							orgNmls,
							orgStateLicense,
							originatorEmail,
							originatorFirstName,
							originatorLastName,
							originatorNmls,
							originatorStateLicense,
							originatorPhone
						);

						setOriginatorData({
							orgName: orgName,
							address: address,
							unitNumber: unitNumber,
							city: city,
							state: state,
							zipcode: zipcode,
							orgNmls: orgNmls,
							orgStateLicense: orgStateLicense,
							originatorEmail: originatorEmail,
							originatorFirstName: originatorFirstName,
							originatorLastName: originatorLastName,
							originatorNmls: originatorNmls,
							originatorStateLicense: originatorStateLicense,
							originatorPhone: originatorPhone,
						});
					}

					let name = getValues?.response?.find(
						(item) => item?.fieldId === "TPO.X61"
					)?.value;
					let phone = getValues?.response?.find(
						(item) => item?.fieldId === "TPO.X65"
					)?.value;
					let email = getValues?.response?.find(
						(item) => item?.fieldId === "TPO.X63"
					)?.value;

					console.log("330 additional values:", name, phone, email);
					setAdditionalContact({ name: name, phone: phone, email: email });

					for (let index = 0; index < (borrowerPairs || []).length; index++) {
						const item = borrowerPairs[index];
						const updatedItem = { ...item };

						if (getValues?.response[index]?.value === "Yes") {
							updatedItem.urla2020CitizenshipResidencyType = "Foreign National";
							foreignIndex.push(index);
						}

						updatedBorrowerPairs.push(updatedItem);
					}

					console.log("250 updated foreign index:", foreignIndex);
					console.log("250 updated borrower pair:", updatedBorrowerPairs);
					setForeignNationalIndex(foreignIndex);
					setLoanApplicationData(updatedBorrowerPairs);
				}
			} catch (err) {
				console.error(
					"270 error (borrower info hydration partially failed):",
					err
				);
			}
		};

		console.log("465 loan guid:", loanGuid);
		console.log("465 user details:", userDetails);

		if (
			loanGuid !== null &&
			loanGuid !== undefined &&
			loanGuid !== "" &&
			userDetails !== undefined &&
			userDetails !== null
		) {
			getLoanDetails();
		}
	}, [loanGuid, userDetails]);

	const updateField = async (
		loanGuid,
		apiObj,
		shouldAdvance = true,
		submittedLoanOriginator,
		submittedLoanProcessor
	) => {
		let salesRepName =
			manualOriginator &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
				? organizationDetails?.sales_rep_name
				: loanOriginator?.organization?.sales_rep_name;

		let valuesArr = [
			{
				id: "CX.PROPERTY.COMMERCIAL.UNITS",
				value: apiObj?.property?.financedNumberOfUnits || "",
			},
			{ id: "NEWHUD.X223", value: "" },
			{ id: "NEWHUD.X224", value: "" },
			{ id: "389", value: "" },
			{ id: "1620", value: "" },
			{
				id: 315,
				// LC-481: keep the DBA chosen when the loan was created
				value:
					savedDbaCompanyName ||
					getOriginatorCompanyName(apiObj, loanOriginator?.organization),
			},
			{ id: 313, value: loanOriginator?.organization?.city },
			{ id: 321, value: loanOriginator?.organization?.state },
			{ id: 323, value: loanOriginator?.organization?.zip },
			{ id: 3237, value: loanOriginator?.organization?.nmls_id },
			{ id: 1256, value: salesRepName },
			// {
			// 	id: "CX.LC.AE.EMAIL.COMPANY",
			// 	value: organizationDetails?.sales_rep_email,
			// },
		];

		// Persist the LO/LP selection ids so they can be re-hydrated when the
		// user returns to this step (the create flow already saves these).
		if (
			loanOriginatorId !== null &&
			loanOriginatorId !== undefined &&
			loanOriginatorId !== ""
		) {
			valuesArr.push({ id: "TPO.X62", value: String(loanOriginatorId) });
		}

		if (
			loanProcessorId !== null &&
			loanProcessorId !== undefined &&
			loanProcessorId !== ""
		) {
			valuesArr.push({ id: "TPO.X75", value: String(loanProcessorId) });
		}

		// if (
		// 	childOrganizationName ||
		// 	selectedUser(userDetails, impersonatedUser)?.child_org_name !== ""
		// ) {
		// 	valuesArr.push({
		// 		id: "CX.LC.AE.EMAIL.BRANCH",
		// 		value: childOrganizationDetails?.sales_rep_email,
		// 	});
		// }

		if (
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR
		) {
			valuesArr.push({
				id: "CX.LOLP.SELECT.MANUAL.METHOD",
				value: manualOriginator ? "Y" : "N",
			});

			if (manualOriginator) {
				valuesArr.push({
					id: "315",
					value: originatorData?.orgName,
				});
				valuesArr.push({
					id: "313",
					value: originatorData?.city,
				});
				valuesArr.push({
					id: "321",
					value: originatorData?.state,
				});
				valuesArr.push({
					id: "323",
					value: originatorData?.zipcode,
				});
				valuesArr.push({
					id: "3237",
					value: originatorData?.orgNmls,
				});
			}
		}

		console.log("300 foreign national:", foreignNationalIndex);

 		/*** Foreign National Citizenship parameter CX.CITIZENSHIP CX.FOREIGNNATL configuration START ***/
 
		// Initialize hasForeignNational as "No"
		var hasForeignNational = "No";
 
		// Loop through the range of indices (0 to 5) for 3 borrower pairs
		for (let index = 0; index < 6; index++) {
			// Create the id based on the index
			const id = `CX.FOREIGNNATLBORR${index + 1}`;
 
			// Set value to "Yes" if index is in foreignNationalIndex, otherwise "No"
			const value = foreignNationalIndex.includes(index) ? "Yes" : "No";
 
			// If any borrower is "Yes", update hasForeignNational
			if (value === "Yes") {
				hasForeignNational = "Yes";
			}
 
			// Push borrower-specific value
			valuesArr.push({ id, value });
		}
 
		// Push overall CX.FOREIGNNATL value
		valuesArr.push({
			id: "CX.FOREIGNNATL",
			value: hasForeignNational
		});
 
		const borrower1Citizenship = loanApplicationData[0]?.urla2020CitizenshipResidencyType;
		console.log("Borrower Details: " , borrower1Citizenship);
		const firstBorrowerCitizenShipType = borrowerCitizenshipTypeOptions.find(option => option.value === borrower1Citizenship);
		console.log("Borrower aa: " , firstBorrowerCitizenShipType);
 
		const foreignNotionalFirstBorrower = foreignNationalIndex.includes(0) ? "Yes" : "No";
		valuesArr.push({
			id: "CX.CITIZENSHIP",
			value: foreignNotionalFirstBorrower === "Yes" ? "Foreign National" : firstBorrowerCitizenShipType?.label
		});
 
		 /***  Foreign National Citizenship parameter CX.CITIZENSHIP CX.FOREIGNNATL configuration END ***/	
		
		 // Added blank values for below fields - LC 206
		valuesArr.push({
			id: "URLA.X189",
			value: "",
		});
		valuesArr.push({
			id: "URLA.X190",
			value: "",
		});
		console.log("340 loan update values:", valuesArr);
		await updateFirstTimeHomebuyerFields();

		const updateFieldValues = await updateFields({
			loan_guid: loanGuid,
			field_values: valuesArr,
			selectedOrganization: currentOrganization,
			userDetails,
		}).unwrap();
		console.log("90 file res:", updateFieldValues);

		if (updateFieldValues?.response === "Field values updated successfully.") {
			// State may lag behind the LO/LP resolved in handleNext, so prefer those.
			const originator = submittedLoanOriginator || loanOriginator;
			const processor = submittedLoanProcessor || loanProcessor;

			let aeEmailData = null;
			try {
				aeEmailData = await fetchAEEmails({
					broker_id:
						userDetails?.organizations?.find(org => org.name === currentOrganization)?.tpo_broker_id ||
						userDetails?.user_tpo_id ||
						impersonatedUser?.user_tpo_id,
					loan_processor_broker_id: loanOriginatorId,
				}).unwrap();
			} catch (err) {
				console.error("[AE Email] Fetch failed:", err);
			}

			const aeEntityId =
				aeEmailData?.identifiers?.loan_processor_sales_rep_entity_id ||
				originator?.organization?.sales_rep_id;

			try {
				await updateMilestoneFreeRoles({
					loan_guid: loanGuid,
					loan_officer: {
						entityId: loanOriginatorId,
						email: originator?.user?.email || loanOriginatorEmail,
					},
					loan_processor: {
						entityId: loanProcessorId,
						email: processor?.user?.email || loanProcessorEmail,
					},
					account_executive: {
						entityId: aeEntityId,
						email:
							aeEmailData?.loan_processor_ae_email ||
							aeEmailData?.ae_email ||
							"",
					},
				}).unwrap();
			} catch (err) {
				console.error("[Milestone Free Roles] Sync failed:", err);
			}

			try {
				const isManualNdcOrCorr =
					manualOriginator &&
					(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
						?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
						selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
							?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR);

				const tpoFileContactValues = buildTpoFileContactFieldValues({
					aeEmailData,
					aeEntityId,
					organizationId: isManualNdcOrCorr
						? organizationDetails?.organization_id
						: originator?.organization?.organization_id,
				});

				if (tpoFileContactValues) {
					await updateFields({
						loan_guid: loanGuid,
						field_values: tpoFileContactValues,
						selectedOrganization: currentOrganization,
						userDetails,
					}).unwrap();
				}
			} catch (err) {
				console.error("[TPO File Contacts] Sync failed:", err);
			}
			setError("");
			if (shouldAdvance) {
				window.scrollTo({
					top: 0,
					left: 0,
					behavior: "smooth",
				});
				setLoanApplicationStage(currentStage + 1);
			}
			return true;
		}

		setError(updateFieldValues?.response);
		return false;
	};

	const handleNext = async (shouldAdvance = true, skipValidation = false) => {
		setError("");

		console.log("340 application data:", applicationData);
		console.log("340 loanApplication data:", loanApplicationData);

		if (
			!skipValidation &&
			!requiredFields(
				loanApplicationData,
				propertyInfo,
				grossRent,
				homeownersInsurance,
				propertyTaxes,
				loanOriginatorEmail,
				loanProcessorEmail,
				manualOriginator,
				originatorData,
				setError,
				firstTimeHomebuyerSelections.current
			)
		) {
			return false;
		}

		if (
			!skipValidation &&
			manualOriginator &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			if (!requiredFieldsForManualLoanOriginator(originatorData, setError)) {
				return false;
			}
		}

		if (
			!skipValidation &&
			additionalContact?.name !== "" &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			if (!requiredFieldsForAdditionalContact(additionalContact, setError)) {
				return false;
			}
		}

		// Safeguard: the names built below map to Encompass TPO.X61 (LO) and
		// TPO.X74 (LP). If the in-memory LO/LP objects were lost (e.g. after
		// navigating away and back), re-resolve them from the broker list API
		// before building the payload so "undefined undefined" is never submitted.
		const isNdcOrCorrChannel =
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR;

		let effectiveLoanOriginator = loanOriginator;
		let effectiveLoanProcessor = loanProcessor;

		if (
			!isNdcOrCorrChannel &&
			isInvalidLoLpName(buildLoLpFullName(effectiveLoanOriginator))
		) {
			effectiveLoanOriginator =
				resolveBrokerFromList(
					organizationUsers,
					loanOriginatorId,
					loanOriginatorEmail
				) || effectiveLoanOriginator;

			if (isInvalidLoLpName(buildLoLpFullName(effectiveLoanOriginator))) {
				try {
					const refreshedOriginators = await getBrokerListByTitleBranch({
						orgName: brokerSearchOrgName,
						title: "Loan Officer",
						include_child: includeChildren,
					}).unwrap();

					effectiveLoanOriginator =
						resolveBrokerFromList(
							refreshedOriginators,
							loanOriginatorId,
							loanOriginatorEmail
						) || effectiveLoanOriginator;
				} catch (err) {
					console.error("LO details refresh before submit failed:", err);
				}
			}

			if (!isInvalidLoLpName(buildLoLpFullName(effectiveLoanOriginator))) {
				setLoanOriginator(effectiveLoanOriginator);
			}
		}

		if (isInvalidLoLpName(buildLoLpFullName(effectiveLoanProcessor))) {
			effectiveLoanProcessor =
				resolveBrokerFromList(
					organizationProcessor,
					loanProcessorId,
					loanProcessorEmail
				) || effectiveLoanProcessor;

			if (isInvalidLoLpName(buildLoLpFullName(effectiveLoanProcessor))) {
				try {
					const refreshedProcessors = await getBrokerListByTitleBranch({
						orgName: brokerSearchOrgName,
						title: "Loan Processor",
						include_child: includeChildren,
					}).unwrap();

					effectiveLoanProcessor =
						resolveBrokerFromList(
							refreshedProcessors,
							loanProcessorId,
							loanProcessorEmail
						) || effectiveLoanProcessor;
				} catch (err) {
					console.error("LP details refresh before submit failed:", err);
				}
			}

			if (!isInvalidLoLpName(buildLoLpFullName(effectiveLoanProcessor))) {
				setLoanProcessor(effectiveLoanProcessor);
			}
		}

		if (
			(!isNdcOrCorrChannel &&
				isInvalidLoLpName(buildLoLpFullName(effectiveLoanOriginator))) ||
			isInvalidLoLpName(buildLoLpFullName(effectiveLoanProcessor))
		) {
			console.error(
				"Blocking submission: invalid LO/LP name for TPO.X61/TPO.X74",
				{
					loName: buildLoLpFullName(effectiveLoanOriginator),
					lpName: buildLoLpFullName(effectiveLoanProcessor),
				}
			);
			setError(
				"Loan Officer / Loan Processor details could not be verified. Please reselect the Loan Originator and Loan Processor, then try again."
			);
			return false;
		}

		let borrowerPairData = [];

		console.log("445 loan applicationData:", loanApplicationData);
		let applications = [];

		for (let index = 0; index < loanApplicationData.length; index += 2) {
			if (
				loanApplicationData[index]?.urla2020CitizenshipResidencyType ===
				"Foreign National"
			) {
				loanApplicationData[index].urla2020CitizenshipResidencyType =
					"NonPermanentResidentAlien";
			}

			if (
				loanApplicationData[index + 1]?.urla2020CitizenshipResidencyType ===
				"Foreign National"
			) {
				loanApplicationData[index + 1].urla2020CitizenshipResidencyType =
					"NonPermanentResidentAlien";
			}

			applications.push({
				borrower: loanApplicationData[index],
				coBorrower: loanApplicationData[index + 1],
			});

			borrowerPairData.push([
				{
					borrower: loanApplicationData[index],
					coborrower: loanApplicationData[index + 1],
				},
			]);
		}

		console.log("415 applicationData:", applicationData);
		console.log("415 borrower pair data;", borrowerPairData);

		// Update the monthly payment for the specified fee type
		let updatedFees = [];

		if (
			applicationData?.fees === undefined ||
			applicationData?.fees?.length === 0
		) {
			updatedFees.push({
				feeType: "FloodInsurance",
				monthlyPayment: floodInsurance,
			});

			updatedFees.push({
				feeType: "PropertyTaxes",
				monthlyPayment: propertyTaxes,
			});
		} else {
			applicationData?.fees?.map((fee) => {
				if (fee.feeType === "FloodInsurance") {
					updatedFees.push({ ...fee, monthlyPayment: floodInsurance });
				}

				if (fee.feeType === "PropertyTaxes") {
					updatedFees.push({ ...fee, monthlyPayment: propertyTaxes });
				}

				return fee;
			});
		}

		console.log("430 updated fees:", updatedFees);

		setApplicationData({
			...applicationData,
			subjectPropertyGrossRentalIncomeAmount: grossRent,
			proposedHazardInsuranceAmount: homeownersInsurance,
			proposedDuesAmount: hoaDues,
			fees: updatedFees,
			proposedRealEstateTaxesAmount:
				updatedFees?.find((item) => item?.feeType === "PropertyTaxes")
					?.monthlyPayment || "0",
			applications,
			property: propertyInfo,
			tPO: {
				lOEmail: loanOriginatorEmail,
				lPEmail: loanProcessorEmail,
			},
		});

		console.log("305 loan guid:", loanGuid);

		let tpoData = {};

		if (
			manualOriginator &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			tpoData["companyName"] = organizationDetails?.name;
			tpoData["companyId"] = organizationDetails?.company_id;
			tpoData["companyOrganizationId"] = organizationDetails?.organization_id;
			tpoData["companyLegalName"] = organizationDetails?.company_legal_name;
			tpoData["companyAddress"] = organizationDetails?.address;
			tpoData["companyCity"] = organizationDetails?.city;
			tpoData["companyState"] = organizationDetails?.state;
			tpoData["companyZip"] = organizationDetails?.zip;
			tpoData["companyPhone"] = organizationDetails?.phone;
			tpoData["companyAeName"] = organizationDetails?.sales_rep_name;
			tpoData["companyAeUserName"] = organizationDetails?.sales_rep_id;
		} else if (
			isExecutiveOrManager ||
			isTpoSecondary ||
			selectedUser(userDetails, impersonatedUser)?.child_org_name !== ""
		) {
			tpoData["companyName"] = organizationDetails?.name;
			tpoData["companyId"] = organizationDetails?.company_id;
			tpoData["companyOrganizationId"] = organizationDetails?.organization_id;
			tpoData["companyLegalName"] = organizationDetails?.company_legal_name;
			tpoData["companyAddress"] = organizationDetails?.address;
			tpoData["companyCity"] = organizationDetails?.city;
			tpoData["companyState"] = organizationDetails?.state;
			tpoData["companyZip"] = organizationDetails?.zip;
			tpoData["companyPhone"] = organizationDetails?.phone;
			tpoData["companyAeName"] = organizationDetails?.sales_rep_name;
			tpoData["companyAeUserName"] = organizationDetails?.sales_rep_id;
		} else {
			tpoData["companyName"] = effectiveLoanOriginator?.organization?.name;
			tpoData["companyId"] = effectiveLoanOriginator?.organization?.company_id;
			tpoData["companyOrganizationId"] =
				effectiveLoanOriginator?.organization?.organization_id;
			tpoData["companyLegalName"] =
				effectiveLoanOriginator?.organization?.company_legal_name;
			tpoData["companyAddress"] = effectiveLoanOriginator?.organization?.address;
			tpoData["companyCity"] = effectiveLoanOriginator?.organization?.city;
			tpoData["companyState"] = effectiveLoanOriginator?.organization?.state;
			tpoData["companyZip"] = effectiveLoanOriginator?.organization?.zip;
			tpoData["companyPhone"] = effectiveLoanOriginator?.organization?.phone;
			tpoData["companyAeName"] = effectiveLoanOriginator?.organization?.sales_rep_name;
			tpoData["companyAeUserName"] = effectiveLoanOriginator?.organization?.sales_rep_id;
		}

		if (
			((isExecutiveOrManager || isTpoSecondary) && childOrganizationName) ||
			selectedUser(userDetails, impersonatedUser)?.child_org_name !== ""
		) {
			tpoData["branchName"] = childOrganizationDetails?.name;
			tpoData["branchId"] = childOrganizationDetails?.company_id;
			tpoData["branchOrganizationId"] =
				childOrganizationDetails?.organization_id;
			tpoData["branchLegalName"] = childOrganizationDetails?.company_legal_name;
			tpoData["branchAddress"] = childOrganizationDetails?.address;
			tpoData["branchCity"] = childOrganizationDetails?.city;
			tpoData["branchState"] = childOrganizationDetails?.state;
			tpoData["branchZip"] = childOrganizationDetails?.zip;
			tpoData["branchPhone"] = childOrganizationDetails?.phone;
			tpoData["branchAeName"] = childOrganizationDetails?.sales_rep_name;
			tpoData["branchAeUserName"] = childOrganizationDetails?.sales_rep_id;
		}

		if (
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR
		) {
			tpoData["loEmail"] = additionalContact?.email;
			tpoData["loName"] = additionalContact?.name;
			tpoData["loBusinessPhone"] = additionalContact?.phone;
		} else {
			tpoData["loEmail"] = effectiveLoanOriginator?.user?.email;
			tpoData["loStatus"] = effectiveLoanOriginator?.login_enabled;
			tpoData["loAddress"] = effectiveLoanOriginator?.address;
			tpoData["loCity"] = effectiveLoanOriginator?.city;
			tpoData["loState"] = effectiveLoanOriginator?.state;
			tpoData["loZip"] = effectiveLoanOriginator?.zip_code;
			tpoData["loName"] = buildLoLpFullName(effectiveLoanOriginator);
			tpoData["loBusinessPhone"] = effectiveLoanOriginator?.phone;
		}

		console.log("1120 loanProcessor email:", loanProcessorEmail);
		console.log("1120 loanProcessor:", effectiveLoanProcessor);

		tpoData["lpEmail"] = effectiveLoanProcessor?.user?.email;
		tpoData["lpStatus"] = effectiveLoanProcessor?.login_enabled;
		tpoData["lpAddress"] = effectiveLoanProcessor?.address;
		tpoData["lpCity"] = effectiveLoanProcessor?.city;
		tpoData["lpState"] = effectiveLoanProcessor?.state;
		tpoData["lpZip"] = effectiveLoanProcessor?.zip_code;
		tpoData["lpName"] = buildLoLpFullName(effectiveLoanProcessor);
		tpoData["lpBusinessPhone"] = effectiveLoanProcessor?.phone;

		console.log("700 tpoData:", tpoData);

		let originatorInfoData = {};

		if (
			manualOriginator &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			originatorInfoData["originatorAddressLineText"] = originatorData?.address;
			originatorInfoData["originatorAddressUnitIdentifier"] =
				originatorData?.unitNumber;
			originatorInfoData["interviewersCompanyStateLicense"] =
				originatorData?.orgStateLicense;
			originatorInfoData["interviewerEmail"] = originatorData?.originatorEmail;
			originatorInfoData["originatorFirstName"] =
				originatorData?.originatorFirstName;
			originatorInfoData["originatorLastName"] =
				originatorData?.originatorLastName;
			originatorInfoData["nmlsLoanOriginatorId"] =
				originatorData?.originatorNmls;
			originatorInfoData["interviewerLicenseIdentifier"] =
				originatorData?.originatorStateLicense;
			originatorInfoData["interviewerPhoneNumber"] =
				originatorData?.originatorPhone;
		} else {
			originatorInfoData["originatorAddressLineText"] =
				effectiveLoanOriginator?.organization?.address;
			originatorInfoData["interviewerEmail"] =
				effectiveLoanOriginator?.user?.email;
			originatorInfoData["interviewerName"] = buildLoLpFullName(
				effectiveLoanOriginator
			);
			originatorInfoData["originatorFirstName"] =
				effectiveLoanOriginator?.user?.first_name;
			originatorInfoData["originatorLastName"] =
				effectiveLoanOriginator?.user?.last_name;
			originatorInfoData["nmlsLoanOriginatorId"] =
				effectiveLoanOriginator?.nmls_id;

			originatorInfoData["interviewerPhoneNumber"] =
				effectiveLoanOriginator?.phone;
			originatorInfoData["interviewerLicenseIdentifier"] = "";
		}

		console.log("750 originatorInfoData:", originatorInfoData);

		let apiObj = {
			...applicationData,
			subjectPropertyGrossRentalIncomeAmount: grossRent,
			proposedHazardInsuranceAmount: homeownersInsurance,
			proposedDuesAmount: hoaDues,
			fees: updatedFees,
			proposedRealEstateTaxesAmount:
				updatedFees?.find((item) => item?.feeType === "PropertyTaxes")
					?.monthlyPayment || "0",
			applications: applications[0],
			property: propertyInfo,
		};

		// update loan api
		console.log("510 loan id:", loanGuid);
		console.log("510 loan data:", apiObj);

		console.log("430 application data:", applicationData);
		console.log("430 applications:", applications);

		console.log("475 api obj:", {
			...applicationData,
			subjectPropertyGrossRentalIncomeAmount: grossRent,
			proposedHazardInsuranceAmount: homeownersInsurance,
			proposedDuesAmount: hoaDues,
			fees: updatedFees,
			proposedRealEstateTaxesAmount:
				updatedFees?.find((item) => item?.feeType === "PropertyTaxes")
					?.monthlyPayment || "0",
			applications,
		});

		const loanUpdateResponse = await updateLoan({
			loanGuid,
			selectedOrganization: currentOrganization,
			userDetails,
			loanData: {
				subjectPropertyGrossRentalIncomeAmount: grossRent,
				proposedHazardInsuranceAmount: homeownersInsurance,
				proposedDuesAmount: hoaDues,
				fees: updatedFees,
				proposedRealEstateTaxesAmount:
					updatedFees?.find((item) => item?.feeType === "PropertyTaxes")
						?.monthlyPayment || "0",
				property: propertyInfo,
				tpo: {
					loAeName: effectiveLoanOriginator?.organization?.sales_rep_name,
					...tpoData,
				},
				...originatorInfoData,
			},
		}).unwrap();

		console.log("440 loan update res:", loanUpdateResponse);

		console.log("440 borrower pair data:", borrowerPairData);
		console.log("440 borrower response data:", borrowerResponseData);

		let borrowerPair;

		for (let index = 0; index < borrowerPairData.length; index++) {
			console.log("445 index:", index);
			console.log(
				"450 borrower pair id:",
				borrowerResponseData[index]?.borrowerPairId
			);

			if (
				borrowerResponseData[index]?.borrowerPairId !== null &&
				borrowerResponseData[index]?.borrowerPairId !== undefined &&
				borrowerResponseData[index]?.borrowerPairId !== ""
			) {
				// if borrower pair id exists then update the pair
				try {
					borrowerPair = await updateBorrowerPair({
						loanGuid: loanGuid,
						borrowerPairId: borrowerResponseData[index].borrowerPairId,
						loanData: { ...borrowerPairData[index][0] },
						orgName: currentOrganization,
						userDetails,
					}).unwrap();
				} catch (err) {
					console.log("450 err:", err);
					if (err?.data?.details?.includes("was not found")) {
						// if borrower pair id does not exists then create the pair
						borrowerPair = await createBorrowerPair({
							loanGuid: loanGuid,
							data: { ...borrowerPairData[index][0] },
							selectedOrganization: currentOrganization,
							userDetails,
						}).unwrap();
					} else {
						setError(err?.data?.details);
					}
				}
			} else {
				// if borrower pair id does not exists then create the pair
				borrowerPair = await createBorrowerPair({
					loanGuid: loanGuid,
					data: { ...borrowerPairData[index][0] },
					selectedOrganization: currentOrganization,
					userDetails,
				}).unwrap();
			}
		}

		console.log("550 update borrower pair data:", borrowerPair);

		if (borrowerPair !== null && borrowerPair !== undefined) {
			return updateField(
				loanGuid,
				apiObj,
				shouldAdvance,
				effectiveLoanOriginator,
				effectiveLoanProcessor
			);
		}

		return false;
	};

	const validateSection = useCallback(() => {
		return runSilentValidation([
			(setError) =>
				requiredFields(
					loanApplicationData,
					propertyInfo,
					grossRent,
					homeownersInsurance,
					propertyTaxes,
					loanOriginatorEmail,
					loanProcessorEmail,
					manualOriginator,
					originatorData,
					setError,
					firstTimeHomebuyerSelections.current
				),
			(setError) => {
				if (
					manualOriginator &&
					(selectedUserWithLoanChannel(
						userDetails,
						impersonatedUser,
						selectedOrganization
					)?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
						selectedUserWithLoanChannel(
							userDetails,
							impersonatedUser,
							selectedOrganization
						)?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
				) {
					return requiredFieldsForManualLoanOriginator(originatorData, setError);
				}

				return true;
			},
			(setError) => {
				if (
					additionalContact?.name !== "" &&
					(selectedUserWithLoanChannel(
						userDetails,
						impersonatedUser,
						selectedOrganization
					)?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
						selectedUserWithLoanChannel(
							userDetails,
							impersonatedUser,
							selectedOrganization
						)?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
				) {
					return requiredFieldsForAdditionalContact(additionalContact, setError);
				}

				return true;
			},
		]);
	}, [
		additionalContact,
		grossRent,
		homeownersInsurance,
		impersonatedUser,
		loanApplicationData,
		loanOriginatorEmail,
		loanProcessorEmail,
		manualOriginator,
		originatorData,
		propertyInfo,
		propertyTaxes,
		selectedOrganization,
		userDetails,
	]);

	const highlightSectionErrors = useCallback(() => {
		setError("");

		if (
			!requiredFields(
				loanApplicationData,
				propertyInfo,
				grossRent,
				homeownersInsurance,
				propertyTaxes,
				loanOriginatorEmail,
				loanProcessorEmail,
				manualOriginator,
				originatorData,
				setError,
				firstTimeHomebuyerSelections.current
			)
		) {
			return;
		}

		if (
			manualOriginator &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			if (!requiredFieldsForManualLoanOriginator(originatorData, setError)) {
				return;
			}
		}

		if (
			additionalContact?.name !== "" &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			if (!requiredFieldsForAdditionalContact(additionalContact, setError)) {
				return;
			}
		}
	}, [
		additionalContact,
		grossRent,
		homeownersInsurance,
		impersonatedUser,
		loanApplicationData,
		loanOriginatorEmail,
		loanProcessorEmail,
		manualOriginator,
		originatorData,
		propertyInfo,
		propertyTaxes,
		selectedOrganization,
		userDetails,
	]);

	useRegisterLoanSection(registerSectionHandler, {
		validate: validateSection,
		save: () => handleNext(false, true),
		highlightErrors: highlightSectionErrors,
	});

	const handleAddBorrowerPair = () => {
		setLoanApplicationData([...loanApplicationData, {}, {}]);
	};

	const handleRemovePair = async () => {
		console.log("470 pair count:", loanApplicationData?.length / 2 - 1);

		if (tabValue === loanApplicationData?.length / 2 - 1) {
			setTabValue(tabValue - 1);
		}

		let newArr = loanApplicationData;

		if (tabValue === 0) {
			newArr?.splice(tabValue, 2);
		} else {
			newArr?.splice(tabValue * 2, 2);
		}

		setLoanApplicationData([...newArr]);
	};

	const handleRemoveBorrowerPair = async () => {
		console.log("485 tab value:", tabValue);
		console.log("485 borrower id:", borrowerResponseData[tabValue]?.id);

		let deleteBorrowerPairResponse;

		if (borrowerResponseData[tabValue]?.id !== undefined) {
			// if pair exists on loan, remove borrower pair after deleting from the loan
			try {
				deleteBorrowerPairResponse = await deleteBorrowerPair({
					loanGuid: loanGuid,
					borrowerPairId: borrowerResponseData[tabValue]?.id,
					loanData: {},
				}).unwrap();

				console.log("475 delete:", deleteBorrowerPairResponse);

				if (deleteBorrowerPairResponse?.message?.includes("success")) {
					handleRemovePair();
				}
			} catch (err) {
				console.log("515 err:", err);
				if (err?.data?.details?.includes("was not found")) {
					handleRemovePair();
				} else if (
					err?.data?.details?.includes(
						"Loan should have at least one application associated"
					)
				) {
					// show modal with error for deleting borrower pair
					console.log("520 err:", err);
					handleOpenErrorModal();
				}
			}
		} else {
			// if pair is new and does not exist on loan, remove pair without running API
			handleRemovePair();
		}
	};

	function a11yProps(index) {
		return {
			id: `simple-tab-${index}`,
			"aria-controls": `simple-tabpanel-${index}`,
		};
	}

	const handleChangeTab = (event, newValue) => {
		setTabValue(newValue);
	};

	return (
		<Stack direction="column" className={styles.stackContainer}>
			<Stack className={styles.bodyContainer}>
				{/* tabs section */}
				<Tabs
					value={tabValue}
					onChange={handleChangeTab}
					aria-label="basic tabs example"
					TabIndicatorProps={{
						style: {
							backgroundColor: "#49787F",
						},
					}}
				>
					<Tab
						className={styles.tabBtnContainer}
						label={
							<Stack direction="row" className={styles.tabLabelContainer}>
								<Heading6
									text="Borrower Pair 1"
									fontType="semibold"
									extraClass={
										tabValue === 0 ? styles.activeTabText : styles.tabText
									}
								/>

								{tabValue === 0 && (
									<Stack
										className={styles.closeIconContainer}
										onClick={() =>
											loanApplicationData?.length > 2 &&
											handleRemoveBorrowerPair()
										}
									>
										<CloseOutlined className={styles.closeIcon} />
									</Stack>
								)}
							</Stack>
						}
						{...a11yProps(0)}
					/>
					{loanApplicationData?.length > 2 && (
						<Tab
							className={styles.tabBtnContainer}
							label={
								<Stack direction="row" className={styles.tabLabelContainer}>
									<Heading6
										text="Borrower Pair 2"
										fontType="semibold"
										extraClass={
											tabValue === 1 ? styles.activeTabText : styles.tabText
										}
									/>

									{tabValue === 1 && (
										<Stack
											className={styles.closeIconContainer}
											onClick={() => handleRemoveBorrowerPair()}
										>
											<CloseOutlined className={styles.closeIcon} />
										</Stack>
									)}
								</Stack>
							}
							{...a11yProps(1)}
						/>
					)}
					{loanApplicationData?.length > 4 && (
						<Tab
							className={styles.tabBtnContainer}
							label={
								<Stack direction="row" className={styles.tabLabelContainer}>
									<Heading6
										text="Borrower Pair 3"
										fontType="semibold"
										extraClass={
											tabValue === 2 ? styles.activeTabText : styles.tabText
										}
									/>

									{tabValue === 2 && (
										<Stack
											className={styles.closeIconContainer}
											onClick={() => handleRemoveBorrowerPair()}
										>
											<CloseOutlined className={styles.closeIcon} />
										</Stack>
									)}
								</Stack>
							}
							{...a11yProps(2)}
						/>
					)}

					<Box>
						<IconButton
							className={styles.addPairContainer}
							onClick={() =>
								loanApplicationData?.length < 6 && handleAddBorrowerPair()
							}
							disabled={loanApplicationData?.length > 4}
						>
							<AddOutlined className={styles.addPairIcon} />
						</IconButton>
					</Box>
				</Tabs>

				{/* borrower pair 1 */}
				{loanApplicationData?.length > 0 && (
					<TabPanel
						value={tabValue}
						index={0}
						className={tabValue === 0 ? styles.tabPanelContainer : ""}
					>
						<LoanInfoCard loanId={loanGuid} />

						{loanApplicationData?.slice(0, 2)?.map((borrower, index) => (
							<BorrowerInformation
								key={index}
								borrowerIndex={index}
								coBorrower={index % 2 !== 0}
								borrowerData={borrower}
								loanGuid={loanGuid}
								setFirstTimeHomebuyerSelection={setFirstTimeHomebuyerSelection}
								foreignNationalIndex={foreignNationalIndex}
								setForeignNationalIndex={setForeignNationalIndex}
								data={loanApplicationData}
								setData={setLoanApplicationData}
								error={error.includes("provide a value")}
							/>
						))}

						<PropertyDetails
							data={propertyInfo}
							setData={setPropertyInfo}
							loanData={applicationData}
							setLoanData={setApplicationData}
							error={error.includes("provide a value")}
							grossRent={grossRent}
							setGrossRent={setGrossRent}
							homeownersInsurance={homeownersInsurance}
							setHomeownersInsurance={setHomeownersInsurance}
							floodInsurance={floodInsurance}
							setFloodInsurance={setFloodInsurance}
							propertyTaxes={propertyTaxes}
							setPropertyTaxes={setPropertyTaxes}
							realEstateTaxes={realEstateTaxes}
							setRealEstateTaxes={setRealEstateTaxes}
							hoaDues={hoaDues}
							setHoaDues={setHoaDues}
							setHelpText={setHelpText}
							handleOpenHelpText={handleOpenHelpText}
						/>

						<LoanOriginator
							organizationName={currentOrgName}
							organizationDetails={currentOrgDetails}
							loanOriginatorEmail={loanOriginatorEmail}
							setLoanOriginatorEmail={setLoanOriginatorEmail}
							loanOriginatorId={loanOriginatorId}
							setLoanOriginatorId={setLoanOriginatorId}
							organizationUsers={organizationUsers}
							loanOriginator={loanOriginator}
							setLoanOriginator={setLoanOriginator}
							setOrganizationLicense={setOrganizationLicense}
							propertyState={propertyInfo?.state}
							manualOriginator={manualOriginator}
							setManualOriginator={setManualOriginator}
							originatorData={originatorData}
							setOriginatorData={setOriginatorData}
							setHelpText={setHelpText}
							setExtraHelpText={setExtraHelpText}
							handleOpenHelpText={handleOpenHelpText}
							error={error.includes("provide a value")}
							allowManualInput={allowManualInput}
							disabled={!allowManualInput}
						/>

						<LoanProcessor
							organizationName={currentOrgName}
							organizationDetails={currentOrgDetails}
							loanProcessorEmail={loanProcessorEmail}
							setLoanProcessorEmail={setLoanProcessorEmail}
							loanProcessorId={loanProcessorId}
							setLoanProcessorId={setLoanProcessorId}
							organizationProcessor={organizationProcessor}
							loanProcessor={loanProcessor}
							propertyState={propertyInfo?.state}
							error={error.includes("provide a value")}
							disabled={true}
						/>

						{(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
							?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
							selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
								?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR) && (
							<AdditionalLoanContact
								additionalContact={additionalContact}
								setAdditionalContact={setAdditionalContact}
								error={error.includes("provide a value")}
							/>
						)}
					</TabPanel>
				)}

				{/* borrower pair 2 */}
				{loanApplicationData?.length > 2 && (
					<TabPanel
						value={tabValue}
						index={1}
						className={tabValue === 1 ? styles.tabPanelContainer : ""}
					>
						<LoanInfoCard loanId={loanGuid} />

						{loanApplicationData?.slice(2, 4)?.map((borrower, index) => (
							<BorrowerInformation
								key={index}
								borrowerIndex={index + 2}
								coBorrower={index % 2 !== 0}
								borrowerData={borrower}
								loanGuid={loanGuid}
								setFirstTimeHomebuyerSelection={setFirstTimeHomebuyerSelection}
								foreignNationalIndex={foreignNationalIndex}
								setForeignNationalIndex={setForeignNationalIndex}
								data={loanApplicationData}
								setData={setLoanApplicationData}
								error={error.includes("provide a value")}
							/>
						))}

						<PropertyDetails
							data={propertyInfo}
							setData={setPropertyInfo}
							loanData={applicationData}
							setLoanData={setApplicationData}
							error={error.includes("provide a value")}
							grossRent={grossRent}
							setGrossRent={setGrossRent}
							homeownersInsurance={homeownersInsurance}
							setHomeownersInsurance={setHomeownersInsurance}
							floodInsurance={floodInsurance}
							setFloodInsurance={setFloodInsurance}
							propertyTaxes={propertyTaxes}
							setPropertyTaxes={setPropertyTaxes}
							realEstateTaxes={realEstateTaxes}
							setRealEstateTaxes={setRealEstateTaxes}
							hoaDues={hoaDues}
							setHoaDues={setHoaDues}
							setHelpText={setHelpText}
							handleOpenHelpText={handleOpenHelpText}
						/>

						<LoanOriginator
							organizationName={currentOrgName}
							organizationDetails={currentOrgDetails}
							loanOriginatorEmail={loanOriginatorEmail}
							setLoanOriginatorEmail={setLoanOriginatorEmail}
							loanOriginatorId={loanOriginatorId}
							setLoanOriginatorId={setLoanOriginatorId}
							organizationUsers={organizationUsers}
							loanOriginator={loanOriginator}
							setLoanOriginator={setLoanOriginator}
							setOrganizationLicense={setOrganizationLicense}
							propertyState={propertyInfo?.state}
							manualOriginator={manualOriginator}
							setManualOriginator={setManualOriginator}
							originatorData={originatorData}
							setOriginatorData={setOriginatorData}
							setHelpText={setHelpText}
							setExtraHelpText={setExtraHelpText}
							handleOpenHelpText={handleOpenHelpText}
							error={error.includes("provide a value")}
							allowManualInput={allowManualInput}
							disabled={!allowManualInput}
						/>

						<LoanProcessor
							organizationName={currentOrgName}
							organizationDetails={currentOrgDetails}
							loanProcessorEmail={loanProcessorEmail}
							setLoanProcessorEmail={setLoanProcessorEmail}
							loanProcessorId={loanProcessorId}
							setLoanProcessorId={setLoanProcessorId}
							organizationProcessor={organizationProcessor}
							loanProcessor={loanProcessor}
							propertyState={propertyInfo?.state}
							error={error.includes("provide a value")}
							disabled={true}
						/>

						{(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
							?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
							selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
								?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR) && (
							<AdditionalLoanContact
								additionalContact={additionalContact}
								setAdditionalContact={setAdditionalContact}
								error={error.includes("provide a value")}
							/>
						)}
					</TabPanel>
				)}

				{/* borrower pair 3 */}
				{loanApplicationData?.length > 4 && (
					<TabPanel
						value={tabValue}
						index={2}
						className={tabValue === 2 ? styles.tabPanelContainer : ""}
					>
						<LoanInfoCard loanId={loanGuid} />

						{loanApplicationData?.slice(4, 6)?.map((borrower, index = 4) => (
							<BorrowerInformation
								key={index}
								borrowerIndex={index + 4}
								coBorrower={index % 2 !== 0}
								borrowerData={borrower}
								loanGuid={loanGuid}
								setFirstTimeHomebuyerSelection={setFirstTimeHomebuyerSelection}
								foreignNationalIndex={foreignNationalIndex}
								setForeignNationalIndex={setForeignNationalIndex}
								data={loanApplicationData}
								setData={setLoanApplicationData}
								error={error.includes("provide a value")}
							/>
						))}

						<PropertyDetails
							data={propertyInfo}
							setData={setPropertyInfo}
							loanData={applicationData}
							setLoanData={setApplicationData}
							error={error.includes("provide a value")}
							grossRent={grossRent}
							setGrossRent={setGrossRent}
							homeownersInsurance={homeownersInsurance}
							setHomeownersInsurance={setHomeownersInsurance}
							floodInsurance={floodInsurance}
							setFloodInsurance={setFloodInsurance}
							propertyTaxes={propertyTaxes}
							setPropertyTaxes={setPropertyTaxes}
							realEstateTaxes={realEstateTaxes}
							setRealEstateTaxes={setRealEstateTaxes}
							hoaDues={hoaDues}
							setHoaDues={setHoaDues}
							setHelpText={setHelpText}
							handleOpenHelpText={handleOpenHelpText}
						/>

						<LoanOriginator
							organizationName={currentOrgName}
							organizationDetails={currentOrgDetails}
							loanOriginatorEmail={loanOriginatorEmail}
							setLoanOriginatorEmail={setLoanOriginatorEmail}
							loanOriginatorId={loanOriginatorId}
							setLoanOriginatorId={setLoanOriginatorId}
							organizationUsers={organizationUsers}
							loanOriginator={loanOriginator}
							setLoanOriginator={setLoanOriginator}
							setOrganizationLicense={setOrganizationLicense}
							propertyState={propertyInfo?.state}
							manualOriginator={manualOriginator}
							setManualOriginator={setManualOriginator}
							originatorData={originatorData}
							setOriginatorData={setOriginatorData}
							setHelpText={setHelpText}
							setExtraHelpText={setExtraHelpText}
							handleOpenHelpText={handleOpenHelpText}
							error={error.includes("provide a value")}
							allowManualInput={allowManualInput}
							disabled={!allowManualInput}
						/>

						<LoanProcessor
							organizationName={currentOrgName}
							organizationDetails={currentOrgDetails}
							loanProcessorEmail={loanProcessorEmail}
							setLoanProcessorEmail={setLoanProcessorEmail}
							loanProcessorId={loanProcessorId}
							setLoanProcessorId={setLoanProcessorId}
							organizationProcessor={organizationProcessor}
							loanProcessor={loanProcessor}
							propertyState={propertyInfo?.state}
							error={error.includes("provide a value")}
							disabled={true}
						/>

						{(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
							?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
							selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
								?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR) && (
							<AdditionalLoanContact
								additionalContact={additionalContact}
								setAdditionalContact={setAdditionalContact}
								error={error.includes("provide a value")}
							/>
						)}
					</TabPanel>
				)}
			</Stack>

			{error !== "" && (
				<RequiredError
					headerText={error.includes("Loan is locked") ? "" : "Missing fields"}
					text={
						error.includes("Something went wrong")
							? "Updates Not Received by LOS. Please Try Again."
							: error.includes("provide a value")
								? "Please provide a value for all fields highlighted above."
								: error.includes("Loan is locked")
									? "This loan is currently being edited by Logan Finance. Once Logan Finance exits the loan, you will be able to navigate the loan in the system and the data displayed will be updated as needed."
									: error.includes("borrower.dependentCount") &&
										"Please check your dependent count."
					}
				/>
			)}

			<Stack direction="row" className={styles.footerContainer}>
				<PrimaryButton
					text="Save and Continue"
					endIcon={<ArrowForwardOutlined className={styles.nextIcon} />}
					onClick={() => handleNext()}
				/>
			</Stack>

			{/* modals */}
			<LoaderModal
				open={
					isFetchingAEEmails ||
					loadingBorrowerPairData ||
					loadingloanData ||
					isFetchingFields ||
					isUpdatingFields ||
					updatingLoan ||
					updatingBorrowerPair ||
					creatingBorrowerPair ||
					deletingBorrowerPair ||
					brokerListBranchLoading ||
					organizationDetailsLoading ||
					isFetchingAEEmails
				}
			/>
			<ErrorModal
				open={errorModalVisible}
				text="Unable to delete the primary borrower pair"
				handleClose={handleCloseErrorModal}
			/>
			<HelpTextModal
				open={helpTextOpen}
				modalText={helpText}
				extraHelpText={extraHelpText}
				handleClose={handleCloseHelpText}
			/>
		</Stack>
	);
};

export default EditLoanApplication;
